import { webPushApi } from '../api/client';
import { trackCaptureEvent } from './captureAnalytics';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function requestPushPermission(): Promise<'granted' | 'denied' | 'default'> {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.requestPermission();
}

/**
 * Topics a subscription can be narrowed to. Mirrors
 * `storythreads-api/constants/webPushTopics.ts`; omitting them means everything.
 */
export type PushTopic = 'new_takes' | 'outcomes';

export async function subscribeToPush(topics?: PushTopic[]): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }

  // Without a VAPID public key the browser would reject the subscribe with an
  // opaque error. Fail fast (and quietly) so a missing build-time env var
  // degrades to "no push" instead of a noisy exception.
  if (!VAPID_PUBLIC_KEY) {
    // A missing build-time key yields ZERO subscriptions fleet-wide, silently —
    // the whole push channel is dark and nothing says so. Distinct reason code
    // so this is one query away from a deploy misconfiguration.
    trackCaptureEvent('push_subscribe_failed', {
      surface: 'push',
      reason: 'missing_vapid_key',
    });
    return false;
  }

  try {
    const permission = await requestPushPermission();
    if (permission !== 'granted') {
      trackCaptureEvent(
        permission === 'denied' ? 'push_permission_denied' : 'push_subscribe_failed',
        { surface: 'push', permission, ...(permission === 'default' ? { reason: 'dismissed' } : {}) },
      );
      return false;
    }
    trackCaptureEvent('push_permission_granted', { surface: 'push' });

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    });

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys) {
      trackCaptureEvent('push_subscribe_failed', {
        surface: 'push',
        reason: 'incomplete_subscription',
      });
      return false;
    }

    await webPushApi.subscribe(
      json.endpoint,
      { p256dh: json.keys.p256dh!, auth: json.keys.auth! },
      topics,
    );

    return true;
  } catch (err) {
    // Previously fully swallowed: a browser-side subscribe rejection or a
    // backend 4xx both produced a silent `false`.
    trackCaptureEvent('push_subscribe_failed', {
      surface: 'push',
      reason: 'exception',
      message: err instanceof Error ? err.message : String(err),
    });
    return false;
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await webPushApi.unsubscribe(subscription.endpoint);
      await subscription.unsubscribe();
    }

    return true;
  } catch {
    return false;
  }
}

export async function isPushSubscribed(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
}
